import React, { useEffect, useState } from 'react';
import { ArrowDownTrayIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { projectService } from '../services/projectService';
import { formatDate } from '../lib/format';
import { Download, Release } from '../types';
import { EmptyState, ErrorNote, LoadingState, Modal } from './ui';

interface DownloadsModalProps {
  release: Release;
  projectName: string;
  canManage: boolean;
  onClose: () => void;
}

const DownloadsModal: React.FC<DownloadsModalProps> = ({ release, projectName, canManage, onClose }) => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [downloads, setDownloads] = useState<Download[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    projectService
      .getReleaseDownloads(release, canManage ? undefined : user.uid)
      .then((data) => !cancelled && setDownloads(data))
      .catch((err) => {
        console.error('Error loading downloads:', err);
        if (!cancelled) setError(true);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [release, canManage, user]);

  // pdfmake is large, so it is only loaded when a certificate is requested.
  const certificate = async (download: Download) => {
    const { pdfService } = await import('../services/pdfService');
    pdfService.generateDownloadCertificate(
      projectName,
      release.version,
      release.title,
      release.description,
      release.fileName,
      download.downloadedByName,
      download.downloadedByEmail,
      download.downloadedAt,
      download.ipAddress
    );
  };

  return (
    <Modal
      title={t('downloads.title')}
      description={
        <>
          {release.title} <span className="version-tag ml-1">v{release.version}</span>
          {!canManage && <span className="mt-1 block">{t('downloads.onlyYours')}</span>}
        </>
      }
      onClose={onClose}
      size="xl"
    >
      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorNote>{t('downloads.loadFailed')}</ErrorNote>
      ) : downloads.length === 0 ? (
        <EmptyState icon={ArrowDownTrayIcon} title={t('downloads.empty')} text={t('downloads.emptyText')} compact />
      ) : (
        <>
          <div className="-mx-6 overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="pl-6">{t('downloads.who')}</th>
                  <th>{t('downloads.when')}</th>
                  <th>{t('downloads.ip')}</th>
                  <th className="pr-6 text-right">{t('downloads.certificate')}</th>
                </tr>
              </thead>
              <tbody>
                {downloads.map((d) => (
                  <tr key={d.id}>
                    <td className="pl-6">
                      <p className="font-medium text-gray-900 dark:text-gray-100">{d.downloadedByName}</p>
                      <p className="text-xs text-gray-500">{d.downloadedByEmail}</p>
                    </td>
                    <td className="whitespace-nowrap">{formatDate(d.downloadedAt, language, true)}</td>
                    <td className="whitespace-nowrap font-mono text-xs">{d.ipAddress || '—'}</td>
                    <td className="pr-6 text-right">
                      <button type="button" onClick={() => certificate(d)} className="btn btn-secondary btn-sm">
                        <DocumentArrowDownIcon className="h-4 w-4" />
                        PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {t('downloads.total', { count: downloads.length })}
          </p>
        </>
      )}
    </Modal>
  );
};

export default DownloadsModal;
